import { useState, useCallback, useEffect } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import TuneIcon from '@material-ui/icons/Tune';
import ListAltIcon from '@material-ui/icons/ListAlt';
import DescriptionIcon from '@material-ui/icons/Description';
import TimelineIcon from '@material-ui/icons/Timeline';

// ─── Annotations ────────────────────────────────────────────────────────────

const K8S_NAMESPACE_ANNOTATION = 'backstage.io/kubernetes-namespace';
const HUMANITEC_ORG_ANNOTATION = 'humanitec.dev/orgId';
const HUMANITEC_APP_ANNOTATION    = 'humanitec.dev/projectId';
// Optional annotation to override the Humanitec module name (defaults to entity name).
const HUMANITEC_MODULE_ANNOTATION = 'humanitec.dev/moduleId';
// Emitted by humanitecV2EntityProvider; identifies which env this workload lives in.
const HUMANITEC_ENV_ANNOTATION    = 'humanitec.dev/envId';
// All env IDs for this workload (comma-separated), set by entity provider
const K8S_NAMESPACES_ANNOTATION   = 'backstage.io/kubernetes-namespaces';

// Helper: accept both humanitec.dev/* (canonical) and humanitec.com/* (legacy) prefixes
const getHumanitecAnnotation = (annotations: Record<string, string>, key: string): string => {
  return annotations[key] || annotations[key.replace('humanitec.dev/', 'humanitec.com/')] || '';
};



// ─── Predicate exported for EntityPage EntitySwitch ─────────────────────────

export const isDay2ActionsAvailable = (entity: any) => {
  const a = entity?.metadata?.annotations ?? {};
  return Boolean(
    a[K8S_NAMESPACE_ANNOTATION] ||
    a[HUMANITEC_ORG_ANNOTATION] ||
    a['humanitec.com/orgId'],
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    minHeight: 52,
  },
  actionLabel: {
    minWidth: 80,
    fontWeight: 600,
    fontSize: '0.875rem',
    flexShrink: 0,
  },
  actionDesc: {
    flex: 1,
    color: theme.palette.text.secondary,
    fontSize: '0.82rem',
  },
  resultBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    wordBreak: 'break-all',
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  slider: { flex: 1 },
  replicaBadge: {
    minWidth: 32,
    textAlign: 'center',
    fontWeight: 700,
  },
}));

// ─── Types ──────────────────────────────────────────────────────────────────

type ActionResult = { ok: true; message: string; detail?: string } | { ok: false; message: string };

type Deployment = { id: string; status: string; created_at: string };
type HumanitecEnv = { id: string; name: string };
type PodSummary = {
  name: string;
  phase: string;
  ready: boolean;
  restarts: number;
  startTime: string | undefined;
  node: string | undefined;
};
type EventSummary = {
  type: string;
  reason: string;
  message: string;
  count: number;
  lastTimestamp: string | undefined;
  involvedObject: string | undefined;
};

// ─── Component ──────────────────────────────────────────────────────────────

export function Day2ActionsCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch: authedFetch } = useApi(fetchApiRef);

  const annotations = entity.metadata.annotations ?? {};
  const entityName = entity.metadata.name;

  // K8s context
  const k8sNamespace = annotations[K8S_NAMESPACE_ANNOTATION] || 'default';
  const deploymentName = entityName;

  // Humanitec context — handle both humanitec.dev/* and humanitec.com/* prefixes
  const humanitecOrgId     = getHumanitecAnnotation(annotations, HUMANITEC_ORG_ANNOTATION);
  const humanitecAppId     = getHumanitecAnnotation(annotations, HUMANITEC_APP_ANNOTATION) || entityName;
  const humanitecModuleName = getHumanitecAnnotation(annotations, HUMANITEC_MODULE_ANNOTATION) || entityName;
  // Primary env from annotation (entity provider stores first non-prod env here)
  const humanitecEnvId     = getHumanitecAnnotation(annotations, HUMANITEC_ENV_ANNOTATION);
  // All env IDs stored by entity provider as comma-separated list — use as immediate fallback
  const allEnvIds          = annotations[K8S_NAMESPACES_ANNOTATION]
    ? annotations[K8S_NAMESPACES_ANNOTATION].split(',').map(s => s.trim()).filter(Boolean)
    : (humanitecEnvId ? [humanitecEnvId] : []);
  const hasHumanitec = Boolean(humanitecOrgId);
  const hasK8s = Boolean(annotations[K8S_NAMESPACE_ANNOTATION]);

  // ── Restart state ────────────────────────────────────────────────────────
  const [restartOpen, setRestartOpen] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [restartResult, setRestartResult] = useState<ActionResult | null>(null);

  // ── Env state (loaded from Humanitec API, pre-seeded from annotations) ─────
  // Immediately seed envs from the kubernetes-namespaces annotation so the
  // dropdown is populated even before the API responds.
  const seedEnvs: HumanitecEnv[] = allEnvIds.map(id => ({ id, name: id }));
  const [envs, setEnvs] = useState<HumanitecEnv[]>(seedEnvs);
  const [selectedEnv, setSelectedEnv] = useState(humanitecEnvId || allEnvIds[0] || '');

  // ── Scale state ──────────────────────────────────────────────────────────
  const [scaleOpen, setScaleOpen] = useState(false);
  const [scaleLoading, setScaleLoading] = useState(false);
  const [scaleValue, setScaleValue] = useState<number>(2);
  const [scaleResult, setScaleResult] = useState<ActionResult | null>(null);

  // ── Rollback state ───────────────────────────────────────────────────────
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollbackEnv, setRollbackEnv] = useState(humanitecEnvId || allEnvIds[0] || '');
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);
  const [selectedDeploy, setSelectedDeploy] = useState('');
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<ActionResult | null>(null);

  // ── Shared helper ────────────────────────────────────────────────────────
  const getBaseUrl = useCallback(
    () => discovery.getBaseUrl('app'),
    [discovery],
  );
  // Load full environment names from Humanitec API (replaces ID-only seed from annotation)
  useEffect(() => {
    if (!hasHumanitec || !humanitecAppId) return;
    getBaseUrl().then(baseUrl =>
      authedFetch(`${baseUrl}/api/day2-actions/environments?projectId=${encodeURIComponent(humanitecAppId)}`)
        .then(r => r.json())
        .then((data: any[]) => {
          if (Array.isArray(data) && data.length > 0) setEnvs(data);
          // else keep the seeded ID-only list
        })
        .catch(() => { /* keep seed list */ })
    );
  }, [hasHumanitec, humanitecAppId]); // eslint-disable-line react-hooks/exhaustive-deps
  // ── Restart action ───────────────────────────────────────────────────────
  const handleRestart = async () => {
    setRestartLoading(true);
    setRestartResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespace: k8sNamespace, deploymentName }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setRestartResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      } else {
        const pods: string[] = data.pods ?? [];
        setRestartResult({
          ok: true,
          message: data.message,
          detail: pods.length ? `Pods: ${pods.join(', ')}` : undefined,
        });
      }
    } catch (err: any) {
      setRestartResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setRestartLoading(false);
    }
  };

  // ── Scale action — routes to Humanitec or K8s depending on service type ──
  const handleScale = async () => {
    if (hasHumanitec && !selectedEnv) {
      setScaleResult({ ok: false, message: 'Select an environment before scaling.' });
      return;
    }
    setScaleLoading(true);
    setScaleResult(null);
    try {
      const baseUrl = await getBaseUrl();
      if (hasHumanitec) {
        // Humanitec-managed: create a delta + deploy so the change persists
        const resp = await authedFetch(`${baseUrl}/api/day2-actions/scale-humanitec`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            appId: humanitecAppId,
            envId: selectedEnv,
            moduleName: humanitecModuleName,
            replicas: scaleValue,
          }),
        });
        const data = await resp.json();
        if (!resp.ok) {
          setScaleResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
        } else {
          setScaleResult({ ok: true, message: data.message });
        }
      } else {
        // Pure K8s service: direct patch
        const resp = await authedFetch(`${baseUrl}/api/day2-actions/scale`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ namespace: k8sNamespace, deploymentName, replicas: scaleValue }),
        });
        const data = await resp.json();
        if (!resp.ok) {
          setScaleResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
        } else {
          setScaleResult({ ok: true, message: data.message });
        }
      }
    } catch (err: any) {
      setScaleResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setScaleLoading(false);
    }
  };

  // ── Rollback helpers ─────────────────────────────────────────────────────
  const loadDeployments = async (envId: string) => {
    setDeploymentsLoading(true);
    setDeployments([]);
    setSelectedDeploy('');
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/day2-actions/deployments?appId=${encodeURIComponent(humanitecAppId)}&envId=${encodeURIComponent(envId)}`,
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      setDeployments(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setRollbackResult({ ok: false, message: err.message || 'Failed to load deployments' });
    } finally {
      setDeploymentsLoading(false);
    }
  };

  const handleRollback = async () => {
    setRollbackLoading(true);
    setRollbackResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: humanitecAppId,
          envId: rollbackEnv,
          deployId: selectedDeploy,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setRollbackResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      } else {
        setRollbackResult({ ok: true, message: `Rollback to ${selectedDeploy} triggered.` });
        setRollbackOpen(false);
      }
    } catch (err: any) {
      setRollbackResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setRollbackLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <InfoCard
      title="Day 2 Actions"
      subheader={entityName}
      action={
        hasHumanitec ? (
          <FormControl variant="outlined" size="small" style={{ minWidth: 120, margin: '8px 8px 0 0' }}>
            <InputLabel>Environment</InputLabel>
            <Select
              value={selectedEnv}
              onChange={e => setSelectedEnv(e.target.value as string)}
              label="Environment"
            >
              {envs.length > 0
                ? envs.map(env => (
                    <MenuItem key={env.id} value={env.id}>
                      {env.name || env.id}
                    </MenuItem>
                  ))
                : <MenuItem value={humanitecEnvId}>{humanitecEnvId}</MenuItem>}
            </Select>
          </FormControl>
        ) : undefined
      }
    >
      {/* ── Restart ──────────────────────────────────────────────────── */}
      {hasK8s && (
        <>
          <Box className={classes.actionRow}>
            <Typography className={classes.actionLabel}>Restart</Typography>
            <Typography className={classes.actionDesc}>
              Triggers a rolling pod restart for this deployment.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={() => {
                setRestartResult(null);
                setRestartOpen(true);
              }}
            >
              Restart
            </Button>
          </Box>

          {restartResult && (
            <Typography
              className={restartResult.ok ? classes.resultBox : classes.errorText}
            >
              {restartResult.message}
              {restartResult.ok && restartResult.detail && (
                <> — {restartResult.detail}</>
              )}
            </Typography>
          )}

          <Dialog
            open={restartOpen}
            onClose={() => !restartLoading && setRestartOpen(false)}
          >
            <DialogTitle>Confirm Restart</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will trigger a rolling restart of <strong>{deploymentName}</strong> in
                namespace <strong>{k8sNamespace}</strong>. Existing pods will be replaced
                one at a time.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRestartOpen(false)} disabled={restartLoading}>
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={async () => {
                  await handleRestart();
                  setRestartOpen(false);
                }}
                disabled={restartLoading}
                startIcon={restartLoading ? <CircularProgress size={16} /> : undefined}
              >
                {restartLoading ? 'Restarting…' : 'Confirm Restart'}
              </Button>
            </DialogActions>
          </Dialog>

          <Divider />
        </>
      )}

      {/* ── Scale ────────────────────────────────────────────────────── */}
      {(hasK8s || hasHumanitec) && (
        <>
          <Box className={classes.actionRow}>
            <Typography className={classes.actionLabel}>Scale</Typography>
            <Box flex={1}>
              <Box className={classes.sliderRow}>
                <TuneIcon color="action" fontSize="small" />
                <Slider
                  className={classes.slider}
                  min={1} max={10} step={1}
                  value={scaleValue}
                  onChange={(_e, v) => setScaleValue(v as number)}
                  marks valueLabelDisplay="auto"
                />
                <Typography className={classes.replicaBadge}>{scaleValue}</Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                {hasHumanitec
                  ? `Humanitec · module: ${humanitecModuleName}`
                  : 'Replica count 1–10'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<TuneIcon />}
              disabled={hasHumanitec && !selectedEnv}
              onClick={() => { setScaleResult(null); setScaleOpen(true); }}
            >
              Scale
            </Button>
          </Box>

          {scaleResult && (
            <Typography className={scaleResult.ok ? classes.resultBox : classes.errorText}>
              {scaleResult.message}
            </Typography>
          )}

          <Dialog open={scaleOpen} onClose={() => !scaleLoading && setScaleOpen(false)}>
            <DialogTitle>Confirm Scale</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {hasHumanitec
                  ? <>Scale <strong>{humanitecModuleName}</strong> in Humanitec environment <strong>{selectedEnv}</strong> to <strong>{scaleValue}</strong> replica{scaleValue !== 1 ? 's' : ''}? A new deployment will be triggered.</>
                  : <>Scale <strong>{deploymentName}</strong> in namespace <strong>{k8sNamespace}</strong> to <strong>{scaleValue}</strong> replica{scaleValue !== 1 ? 's' : ''}?</>}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setScaleOpen(false)} disabled={scaleLoading}>Cancel</Button>
              <Button
                color="primary" variant="contained"
                onClick={async () => { await handleScale(); setScaleOpen(false); }}
                disabled={scaleLoading || (hasHumanitec && !selectedEnv)}
                startIcon={scaleLoading ? <CircularProgress size={16} /> : undefined}
              >
                {scaleLoading ? 'Scaling…' : `Scale to ${scaleValue}`}
              </Button>
            </DialogActions>
          </Dialog>

          <Divider />
        </>
      )}

      {/* ── Rollback ─────────────────────────────────────────────────── */}
      {hasHumanitec && (
        <>
          <Box className={classes.actionRow}>
            <Typography className={classes.actionLabel}>Rollback</Typography>
            <Typography className={classes.actionDesc}>
              Revert <strong>{humanitecAppId}</strong> to a previous Humanitec deployment.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<RotateLeftIcon />}
              onClick={() => {
                setRollbackResult(null);
                setRollbackEnv(selectedEnv);
                setDeployments([]);
                setSelectedDeploy('');
                setRollbackOpen(true);
                // Auto-load deployments for the selected environment
                if (selectedEnv) loadDeployments(selectedEnv);
              }}
            >
              Rollback
            </Button>
          </Box>

          {rollbackResult && (
            <Typography
              className={rollbackResult.ok ? classes.resultBox : classes.errorText}
            >
              {rollbackResult.message}
            </Typography>
          )}

          <Dialog
            open={rollbackOpen}
            onClose={() => !rollbackLoading && setRollbackOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Rollback Humanitec Deployment</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Select a previous deployment to roll back to for{' '}
                <strong>{humanitecAppId}</strong> in environment{' '}
                <strong>{rollbackEnv || humanitecEnvId}</strong>.
              </DialogContentText>

              {deploymentsLoading && (
                <Box mt={2} display="flex" justifyContent="center">
                  <CircularProgress size={24} />
                </Box>
              )}

              {!deploymentsLoading && deployments.length > 0 && (
                <Box mt={2}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Roll back to deployment</InputLabel>
                    <Select
                      value={selectedDeploy}
                      onChange={e => setSelectedDeploy(e.target.value as string)}
                      label="Roll back to deployment"
                    >
                      {deployments.map(d => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.id} — {d.status} (
                          {new Date(d.created_at).toLocaleString()})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {rollbackResult && !rollbackResult.ok && (
                <Typography className={classes.errorText}>
                  {rollbackResult.message}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRollbackOpen(false)} disabled={rollbackLoading}>
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleRollback}
                disabled={!selectedDeploy || rollbackLoading}
                startIcon={rollbackLoading ? <CircularProgress size={16} /> : undefined}
              >
                {rollbackLoading ? 'Rolling back…' : 'Confirm Rollback'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </InfoCard>
  );
}

// ─── Day2K8sActionsCard ───────────────────────────────────────────────────────
// Restart + scale card for use inside the Kubernetes tab.

export function Day2K8sActionsCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch: authedFetch } = useApi(fetchApiRef);

  const annotations = entity.metadata.annotations ?? {};
  const entityName = entity.metadata.name;
  const k8sNamespace = annotations[K8S_NAMESPACE_ANNOTATION] || 'default';
  const deploymentName = entityName;
  // Humanitec check — if present, direct K8s scale will be overwritten by Humanitec on reconcile
  const hasHumanitec = Boolean(annotations[HUMANITEC_ORG_ANNOTATION]);

  const [restartOpen, setRestartOpen] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [restartResult, setRestartResult] = useState<ActionResult | null>(null);

  const [scaleOpen, setScaleOpen] = useState(false);
  const [scaleLoading, setScaleLoading] = useState(false);
  const [scaleValue, setScaleValue] = useState<number>(2);
  const [scaleResult, setScaleResult] = useState<ActionResult | null>(null);

  // ── Pod status state ────────────────────────────────────────────────────
  const [podsOpen, setPodsOpen] = useState(false);
  const [podsData, setPodsData] = useState<PodSummary[] | null>(null);
  const [podsFetching, setPodsFetching] = useState(false);
  const [podsError, setPodsError] = useState<string | null>(null);

  // ── Logs state ──────────────────────────────────────────────────────────
  const [logsOpen, setLogsOpen] = useState(false);
  const [selectedPod, setSelectedPod] = useState('');
  const [logsData, setLogsData] = useState<string | null>(null);
  const [logsFetching, setLogsFetching] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  // ── Events state ────────────────────────────────────────────────────────
  const [eventsOpen, setEventsOpen] = useState(false);
  const [eventsData, setEventsData] = useState<EventSummary[] | null>(null);
  const [eventsFetching, setEventsFetching] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const getBaseUrl = useCallback(() => discovery.getBaseUrl('app'), [discovery]);

  const handleRestart = async () => {
    setRestartLoading(true);
    setRestartResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespace: k8sNamespace, deploymentName }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setRestartResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      } else {
        const pods: string[] = data.pods ?? [];
        setRestartResult({
          ok: true,
          message: data.message,
          detail: pods.length ? `Pods: ${pods.join(', ')}` : undefined,
        });
      }
    } catch (err: any) {
      setRestartResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setRestartLoading(false);
    }
  };

  const handleScale = async () => {
    setScaleLoading(true);
    setScaleResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/scale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespace: k8sNamespace, deploymentName, replicas: scaleValue }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setScaleResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      } else {
        setScaleResult({ ok: true, message: data.message });
      }
    } catch (err: any) {
      setScaleResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setScaleLoading(false);
    }
  };

  // ── Pod list fetch ──────────────────────────────────────────────────
  const fetchPodList = async (): Promise<PodSummary[]> => {
    setPodsFetching(true);
    setPodsError(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/day2-actions/pods?namespace=${encodeURIComponent(k8sNamespace)}&deploymentName=${encodeURIComponent(deploymentName)}`,
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      const result: PodSummary[] = Array.isArray(data) ? data : [];
      setPodsData(result);
      return result;
    } catch (err: any) {
      setPodsError(err.message || 'Failed to fetch pods');
      return [];
    } finally {
      setPodsFetching(false);
    }
  };

  const handleViewPods = async () => {
    setPodsOpen(true);
    await fetchPodList();
  };

  // ── Log fetch ─────────────────────────────────────────────────────────
  const handleFetchLogs = async (podName: string) => {
    if (!podName) return;
    setLogsFetching(true);
    setLogsData(null);
    setLogsError(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/day2-actions/logs?namespace=${encodeURIComponent(k8sNamespace)}&podName=${encodeURIComponent(podName)}&tailLines=200`,
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      setLogsData(data.logs ?? '');
    } catch (err: any) {
      setLogsError(err.message || 'Failed to fetch logs');
    } finally {
      setLogsFetching(false);
    }
  };

  const handleViewLogs = async () => {
    setLogsData(null);
    setLogsError(null);
    setLogsOpen(true);
    const pods =
      podsData && podsData.length > 0 ? podsData : await fetchPodList();
    if (pods.length > 0) {
      setSelectedPod(pods[0].name);
      await handleFetchLogs(pods[0].name);
    }
  };

  // ── Events fetch ────────────────────────────────────────────────────
  const handleViewEvents = async () => {
    setEventsFetching(true);
    setEventsError(null);
    setEventsData(null);
    setEventsOpen(true);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/day2-actions/events?namespace=${encodeURIComponent(k8sNamespace)}&deploymentName=${encodeURIComponent(deploymentName)}`,
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      setEventsData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setEventsError(err.message || 'Failed to fetch events');
    } finally {
      setEventsFetching(false);
    }
  };

  return (
    <InfoCard title="Operations" subheader={`${deploymentName} · ${k8sNamespace}`}>
      {/* Restart */}
      <Box className={classes.actionRow}>
        <Typography className={classes.actionLabel}>Restart</Typography>
        <Typography className={classes.actionDesc}>
          Triggers a rolling pod restart for this deployment.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={() => { setRestartResult(null); setRestartOpen(true); }}
        >
          Restart
        </Button>
      </Box>
      {restartResult && (
        <Typography className={restartResult.ok ? classes.resultBox : classes.errorText}>
          {restartResult.message}
          {restartResult.ok && restartResult.detail && <> — {restartResult.detail}</>}
        </Typography>
      )}
      <Dialog open={restartOpen} onClose={() => !restartLoading && setRestartOpen(false)}>
        <DialogTitle>Confirm Restart</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will trigger a rolling restart of <strong>{deploymentName}</strong> in
            namespace <strong>{k8sNamespace}</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestartOpen(false)} disabled={restartLoading}>Cancel</Button>
          <Button
            color="primary" variant="contained"
            onClick={async () => { await handleRestart(); setRestartOpen(false); }}
            disabled={restartLoading}
            startIcon={restartLoading ? <CircularProgress size={16} /> : undefined}
          >
            {restartLoading ? 'Restarting…' : 'Confirm Restart'}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider />

      {/* Scale — only for pure K8s services; Humanitec-managed services reconcile away direct K8s patches */}
      {hasHumanitec ? (
        <Box className={classes.actionRow}>
          <Typography className={classes.actionLabel}>Scale</Typography>
          <Typography className={classes.actionDesc} color="textSecondary">
            This service is managed by Humanitec. Scale via the <strong>Platform (v2)</strong> tab
            so the change persists across deployments.
          </Typography>
        </Box>
      ) : (
        <>
          <Box className={classes.actionRow}>
            <Typography className={classes.actionLabel}>Scale</Typography>
            <Box flex={1}>
              <Box className={classes.sliderRow}>
                <TuneIcon color="action" fontSize="small" />
                <Slider
                  className={classes.slider}
                  min={1} max={10} step={1}
                  value={scaleValue}
                  onChange={(_e, v) => setScaleValue(v as number)}
                  marks valueLabelDisplay="auto"
                />
                <Typography className={classes.replicaBadge}>{scaleValue}</Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">Set replica count (1–10)</Typography>
            </Box>
            <Button
              variant="contained" color="primary" size="small"
              startIcon={<TuneIcon />}
              onClick={() => { setScaleResult(null); setScaleOpen(true); }}
            >
              Scale
            </Button>
          </Box>
          {scaleResult && (
            <Typography className={scaleResult.ok ? classes.resultBox : classes.errorText}>
              {scaleResult.message}
            </Typography>
          )}
          <Dialog open={scaleOpen} onClose={() => !scaleLoading && setScaleOpen(false)}>
            <DialogTitle>Confirm Scale</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Scale <strong>{deploymentName}</strong> in namespace{' '}
                <strong>{k8sNamespace}</strong> to <strong>{scaleValue}</strong>{' '}
                replica{scaleValue !== 1 ? 's' : ''}?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setScaleOpen(false)} disabled={scaleLoading}>Cancel</Button>
              <Button
                color="primary" variant="contained"
                onClick={async () => { await handleScale(); setScaleOpen(false); }}
                disabled={scaleLoading}
                startIcon={scaleLoading ? <CircularProgress size={16} /> : undefined}
              >
                {scaleLoading ? 'Scaling…' : `Scale to ${scaleValue}`}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Divider />

      {/* ── Pod Status ──────────────────────────────────────────────────── */}
      <Box className={classes.actionRow}>
        <Typography className={classes.actionLabel}>Pods</Typography>
        <Typography className={classes.actionDesc}>
          Live status of pods for this deployment.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={podsFetching ? <CircularProgress size={14} /> : <ListAltIcon />}
          disabled={podsFetching}
          onClick={handleViewPods}
        >
          View Pods
        </Button>
      </Box>

      <Dialog open={podsOpen} onClose={() => setPodsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Pods — {deploymentName}</DialogTitle>
        <DialogContent>
          {podsFetching && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
          {podsError && <Typography className={classes.errorText}>{podsError}</Typography>}
          {podsData && !podsFetching && (
            podsData.length === 0 ? (
              <Typography>No pods found for <strong>{deploymentName}</strong>.</Typography>
            ) : (
              <Box style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(128,128,128,0.2)' }}>
                      <th style={{ padding: '6px 8px' }}>Name</th>
                      <th style={{ padding: '6px 8px' }}>Phase</th>
                      <th style={{ padding: '6px 8px' }}>Ready</th>
                      <th style={{ padding: '6px 8px' }}>Restarts</th>
                      <th style={{ padding: '6px 8px' }}>Age</th>
                      <th style={{ padding: '6px 8px' }}>Node</th>
                    </tr>
                  </thead>
                  <tbody>
                    {podsData.map(pod => {
                      const diff = pod.startTime
                        ? Date.now() - new Date(pod.startTime).getTime()
                        : 0;
                      const h = Math.floor(diff / 3_600_000);
                      const m = Math.floor((diff % 3_600_000) / 60_000);
                      const age = pod.startTime ? (h > 0 ? `${h}h${m}m` : `${m}m`) : '—';
                      const phaseColor =
                        pod.phase === 'Running' ? '#4caf50' :
                        pod.phase === 'Pending' ? '#ff9800' :
                        pod.phase === 'Failed'  ? '#f44336' : '#9e9e9e';
                      return (
                        <tr key={pod.name} style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                          <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{pod.name}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <Chip
                              size="small"
                              label={pod.phase || '—'}
                              style={{
                                backgroundColor: `${phaseColor}22`,
                                color: phaseColor,
                                border: `1px solid ${phaseColor}66`,
                                fontSize: '0.72rem',
                              }}
                            />
                          </td>
                          <td style={{ padding: '6px 8px', color: pod.ready ? '#4caf50' : '#f44336' }}>
                            {pod.ready ? '✓' : '✗'}
                          </td>
                          <td style={{ padding: '6px 8px', color: pod.restarts > 0 ? '#ff9800' : 'inherit' }}>
                            {pod.restarts}
                          </td>
                          <td style={{ padding: '6px 8px' }}>{age}</td>
                          <td style={{ padding: '6px 8px', opacity: 0.6 }}>{pod.node || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleViewPods}
            disabled={podsFetching}
            startIcon={podsFetching ? <CircularProgress size={14} /> : undefined}
          >
            Refresh
          </Button>
          <Button onClick={() => setPodsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Divider />

      {/* ── Logs ────────────────────────────────────────────────────────── */}
      <Box className={classes.actionRow}>
        <Typography className={classes.actionLabel}>Logs</Typography>
        <Typography className={classes.actionDesc}>
          Recent stdout/stderr from deployment pods (last 200 lines).
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DescriptionIcon />}
          onClick={handleViewLogs}
        >
          View Logs
        </Button>
      </Box>

      <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Logs — {deploymentName}</DialogTitle>
        <DialogContent>
          {podsData && podsData.length > 0 && (
            <Box mb={1} display="flex" alignItems="center" style={{ gap: 8 }}>
              <FormControl variant="outlined" size="small" style={{ minWidth: 280 }}>
                <InputLabel>Pod</InputLabel>
                <Select
                  value={selectedPod}
                  onChange={e => {
                    const name = e.target.value as string;
                    setSelectedPod(name);
                    handleFetchLogs(name);
                  }}
                  label="Pod"
                >
                  {podsData.map(p => (
                    <MenuItem key={p.name} value={p.name}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                onClick={() => selectedPod && handleFetchLogs(selectedPod)}
                disabled={logsFetching || !selectedPod}
                startIcon={logsFetching ? <CircularProgress size={14} /> : undefined}
              >
                Refresh
              </Button>
            </Box>
          )}
          {(podsFetching || logsFetching) && !logsData && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
          {logsError && <Typography className={classes.errorText}>{logsError}</Typography>}
          {logsData !== null && !logsFetching && (
            <pre
              style={{
                fontSize: '0.75rem',
                overflowY: 'auto',
                maxHeight: 420,
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                backgroundColor: 'rgba(0,0,0,0.25)',
                padding: 12,
                borderRadius: 4,
                lineHeight: 1.5,
              }}
            >
              {logsData || '(no log output)'}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Divider />

      {/* ── Events ──────────────────────────────────────────────────────── */}
      <Box className={classes.actionRow}>
        <Typography className={classes.actionLabel}>Events</Typography>
        <Typography className={classes.actionDesc}>
          Recent Kubernetes events for this deployment.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TimelineIcon />}
          onClick={handleViewEvents}
        >
          View Events
        </Button>
      </Box>

      <Dialog open={eventsOpen} onClose={() => setEventsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Events — {deploymentName}</DialogTitle>
        <DialogContent>
          {eventsFetching && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
          {eventsError && <Typography className={classes.errorText}>{eventsError}</Typography>}
          {eventsData && !eventsFetching && (
            eventsData.length === 0 ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                No events found for <strong>{deploymentName}</strong>.
              </Typography>
            ) : (
              <Box style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(128,128,128,0.2)' }}>
                      <th style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>Type</th>
                      <th style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>Reason</th>
                      <th style={{ padding: '6px 8px' }}>Count</th>
                      <th style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>Last Seen</th>
                      <th style={{ padding: '6px 8px' }}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventsData.map((ev, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(128,128,128,0.08)' }}>
                        <td style={{ padding: '6px 8px' }}>
                          <Chip
                            size="small"
                            label={ev.type}
                            style={{
                              backgroundColor: ev.type === 'Warning' ? '#f4433622' : '#4caf5022',
                              color:           ev.type === 'Warning' ? '#f44336'   : '#4caf50',
                              border: '1px solid currentColor',
                              fontSize: '0.72rem',
                            }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{ev.reason}</td>
                        <td style={{ padding: '6px 8px' }}>{ev.count ?? '—'}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', opacity: 0.8 }}>
                          {ev.lastTimestamp ? new Date(ev.lastTimestamp).toLocaleString() : '—'}
                        </td>
                        <td style={{ padding: '6px 8px', maxWidth: 400, wordBreak: 'break-word' }}>{ev.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleViewEvents}
            disabled={eventsFetching}
            startIcon={eventsFetching ? <CircularProgress size={14} /> : undefined}
          >
            Refresh
          </Button>
          <Button onClick={() => setEventsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
}

// ─── Day2RollbackCard ─────────────────────────────────────────────────────────
// Standalone rollback card for use inside the Platform (v2) tab.

export function Day2RollbackCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch: authedFetch } = useApi(fetchApiRef);

  const annotations = entity.metadata.annotations ?? {};
  const entityName = entity.metadata.name;
  const humanitecOrgId = getHumanitecAnnotation(annotations, HUMANITEC_ORG_ANNOTATION);
  const humanitecAppId = getHumanitecAnnotation(annotations, HUMANITEC_APP_ANNOTATION) || entityName;
  const humanitecModuleName = getHumanitecAnnotation(annotations, HUMANITEC_MODULE_ANNOTATION) || entityName;
  const humanitecEnvId = getHumanitecAnnotation(annotations, HUMANITEC_ENV_ANNOTATION);
  const allEnvIds = annotations[K8S_NAMESPACES_ANNOTATION]
    ? annotations[K8S_NAMESPACES_ANNOTATION].split(',').map(s => s.trim()).filter(Boolean)
    : (humanitecEnvId ? [humanitecEnvId] : []);

  // ── Scale state ──────────────────────────────────────────────────────────
  const [scaleHOpen, setScaleHOpen] = useState(false);
  const [scaleHLoading, setScaleHLoading] = useState(false);
  const [scaleHValue, setScaleHValue] = useState<number>(2);
  // Environment comes directly from entity annotation — no user input needed.
  const [scaleHResult, setScaleHResult] = useState<ActionResult | null>(null);

  // ── Rollback state ───────────────────────────────────────────────────────
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollbackEnv, setRollbackEnv] = useState(humanitecEnvId || allEnvIds[0] || '');
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);
  const [selectedDeploy, setSelectedDeploy] = useState('');
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<ActionResult | null>(null);

  const getBaseUrl = useCallback(() => discovery.getBaseUrl('app'), [discovery]);

  // Auto-load the deployment history when we already know the env from the annotation.
  useEffect(() => {
    if (humanitecEnvId) loadDeployments(humanitecEnvId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [humanitecEnvId]);

  const handleScaleHumanitec = async () => {
    setScaleHLoading(true);
    setScaleHResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/scale-humanitec`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: humanitecAppId,
          envId: humanitecEnvId,  // from entity annotation — always correct
          moduleName: humanitecModuleName,
          replicas: scaleHValue,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setScaleHResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      } else {
        setScaleHResult({ ok: true, message: data.message });
      }
    } catch (err: any) {
      setScaleHResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setScaleHLoading(false);
    }
  };

  const loadDeployments = async (envId: string) => {
    setDeploymentsLoading(true);
    setDeployments([]);
    setSelectedDeploy('');
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/day2-actions/deployments?appId=${encodeURIComponent(humanitecAppId)}&envId=${encodeURIComponent(envId)}`,
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      setDeployments(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setRollbackResult({ ok: false, message: err.message || 'Failed to load deployments' });
    } finally {
      setDeploymentsLoading(false);
    }
  };

  const handleRollback = async () => {
    setRollbackLoading(true);
    setRollbackResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(`${baseUrl}/api/day2-actions/rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId: humanitecAppId, envId: rollbackEnv, deployId: selectedDeploy }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setRollbackResult({ ok: false, message: data.error || `HTTP ${resp.status}` });
      } else {
        setRollbackResult({ ok: true, message: `Rollback to ${selectedDeploy} triggered.` });
        setRollbackOpen(false);
      }
    } catch (err: any) {
      setRollbackResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setRollbackLoading(false);
    }
  };

  if (!humanitecOrgId) return null;

  return (
    <InfoCard title="Platform Actions" subheader={humanitecAppId}>
      {/* ── Scale via Humanitec ────────────────────────────────────────────── */}
      <>
        <Box className={classes.actionRow}>
          <Typography className={classes.actionLabel}>Scale</Typography>
          <Box flex={1}>
            <Box className={classes.sliderRow}>
              <TuneIcon color="action" fontSize="small" />
              <Slider
                className={classes.slider}
                min={1} max={10} step={1}
                value={scaleHValue}
                onChange={(_e, v) => setScaleHValue(v as number)}
                marks valueLabelDisplay="auto"
              />
              <Typography className={classes.replicaBadge}>{scaleHValue}</Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              Humanitec · env: {humanitecEnvId} · module: {humanitecModuleName}
            </Typography>
          </Box>
          <Button
            variant="contained" color="primary" size="small"
            startIcon={<TuneIcon />}
            disabled={!humanitecEnvId}
            onClick={() => { setScaleHResult(null); setScaleHOpen(true); }}
          >
            Scale
          </Button>
        </Box>
        {scaleHResult && (
          <Typography className={scaleHResult.ok ? classes.resultBox : classes.errorText}>
            {scaleHResult.message}
          </Typography>
        )}
        <Dialog open={scaleHOpen} onClose={() => !scaleHLoading && setScaleHOpen(false)}>
          <DialogTitle>Confirm Scale</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Scale <strong>{humanitecModuleName}</strong> in Humanitec environment{' '}
              <strong>{humanitecEnvId}</strong> to <strong>{scaleHValue}</strong>{' '}
              replica{scaleHValue !== 1 ? 's' : ''}? A new deployment will be triggered.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScaleHOpen(false)} disabled={scaleHLoading}>Cancel</Button>
            <Button
              color="primary" variant="contained"
              onClick={async () => { await handleScaleHumanitec(); setScaleHOpen(false); }}
              disabled={scaleHLoading || !humanitecEnvId}
              startIcon={scaleHLoading ? <CircularProgress size={16} /> : undefined}
            >
              {scaleHLoading ? 'Scaling…' : `Scale to ${scaleHValue}`}
            </Button>
          </DialogActions>
        </Dialog>
        <Divider />
      </>

      {/* ── Rollback ──────────────────────────────────────────────────── */}
      <Box className={classes.actionRow}>
        <Typography className={classes.actionLabel}>Rollback</Typography>
        <Typography className={classes.actionDesc}>
          Revert <strong>{humanitecAppId}</strong> to a previous Humanitec deployment.
        </Typography>
        <Button
          variant="contained" color="secondary" size="small"
          startIcon={<RotateLeftIcon />}
          onClick={() => {
            setRollbackResult(null);
            setRollbackEnv(humanitecEnvId);
            setDeployments([]);
            setSelectedDeploy('');
            setRollbackOpen(true);
            if (humanitecEnvId) loadDeployments(humanitecEnvId);
          }}
        >
          Rollback
        </Button>
      </Box>
      {rollbackResult && (
        <Typography className={rollbackResult.ok ? classes.resultBox : classes.errorText}>
          {rollbackResult.message}
        </Typography>
      )}
      <Dialog open={rollbackOpen} onClose={() => !rollbackLoading && setRollbackOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rollback Humanitec Deployment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select a previous deployment to roll back to for{' '}
            <strong>{humanitecAppId}</strong> in environment{' '}
            <strong>{rollbackEnv || humanitecEnvId}</strong>.
          </DialogContentText>
          {deploymentsLoading && (
            <Box mt={2} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>
          )}
          {!deploymentsLoading && deployments.length > 0 && (
            <Box mt={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Roll back to deployment</InputLabel>
                <Select value={selectedDeploy} onChange={e => setSelectedDeploy(e.target.value as string)} label="Roll back to deployment">
                  {deployments.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.id} — {d.status} ({new Date(d.created_at).toLocaleString()})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {rollbackResult && !rollbackResult.ok && (
            <Typography className={classes.errorText}>{rollbackResult.message}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackOpen(false)} disabled={rollbackLoading}>Cancel</Button>
          <Button
            color="secondary" variant="contained"
            onClick={handleRollback}
            disabled={!selectedDeploy || rollbackLoading}
            startIcon={rollbackLoading ? <CircularProgress size={16} /> : undefined}
          >
            {rollbackLoading ? 'Rolling back…' : 'Confirm Rollback'}
          </Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
}
